import { db } from "./firebase";
import {
  collection,
  doc,
  getDoc,
  deleteDoc,
  setDoc,
  Timestamp,
} from "firebase/firestore";
import {
  ChangeLogEntry,
  ChangeLogHighlight,
  ChangeLogIndexDoc,
  ChangeLogIndexItem,
} from "@/app/@types/changeLog";

export const saveChangeLogEntry = async ({
  authorId,
  title,
  summary,
  html,
  highlights,
}: {
  authorId: string;
  title: string;
  summary: string;
  html: string;
  highlights?: ChangeLogHighlight[];
}): Promise<ChangeLogEntry> => {
  const createdAt = Timestamp.now();
  const id = createdAt.toMillis().toString();

  const entry: ChangeLogEntry = {
    id,
    title,
    summary,
    html,
    createdAt,
    authorId,
    ...(highlights && highlights.length > 0 ? { highlights } : {}),
  };

  const entryRef = doc(collection(db, "changeLog"), id);
  await setDoc(entryRef, entry);

  const hasHighlights = !!highlights && highlights.length > 0;

  // Index doc lives inside changeLog collection as changeLogIndex
  const indexRef = doc(db, "changeLog", "changeLogIndex");
  const indexSnap = await getDoc(indexRef);
  const existing = (indexSnap.exists()
    ? (indexSnap.data() as ChangeLogIndexDoc).items
    : []) as ChangeLogIndexItem[];

  const updatedItems: ChangeLogIndexItem[] = [
    {
      id,
      title,
      summary,
      createdAt,
      authorId,
      ...(hasHighlights ? { hasHighlights: true } : {}),
    },
    ...existing,
  ];

  await setDoc(
    indexRef,
    {
      items: updatedItems,
      updatedAt: createdAt,
    },
    { merge: true }
  );

  return entry;
};

export const updateChangeLogEntry = async ({
  id,
  summary,
  html,
  highlights,
}: {
  id: string;
  summary: string;
  html: string;
  highlights?: ChangeLogHighlight[];
}): Promise<ChangeLogEntry> => {
  const entryRef = doc(db, "changeLog", id);
  const snap = await getDoc(entryRef);
  if (!snap.exists()) {
    throw new Error("Change log entry not found");
  }
  const existing = snap.data() as ChangeLogEntry;
  const hasHighlights = !!highlights && highlights.length > 0;
  const updated: ChangeLogEntry = {
    ...existing,
    summary,
    html,
    highlights: hasHighlights ? highlights : [],
  };

  await setDoc(entryRef, updated, { merge: true });

  const indexRef = doc(db, "changeLog", "changeLogIndex");
  const indexSnap = await getDoc(indexRef);
  const existingIndex = (indexSnap.exists()
    ? (indexSnap.data() as ChangeLogIndexDoc).items
    : []) as ChangeLogIndexItem[];

  const updatedItems = existingIndex.map((item) =>
    item.id === id
      ? { ...item, summary, hasHighlights }
      : item
  );

  await setDoc(
    indexRef,
    {
      items: updatedItems,
      updatedAt: Timestamp.now(),
    },
    { merge: true }
  );

  return updated;
};

export const deleteChangeLogEntry = async (id: string): Promise<void> => {
  const entryRef = doc(db, "changeLog", id);
  await deleteDoc(entryRef);

  const indexRef = doc(db, "changeLog", "changeLogIndex");
  const indexSnap = await getDoc(indexRef);
  const existingIndex = (indexSnap.exists()
    ? (indexSnap.data() as ChangeLogIndexDoc).items
    : []) as ChangeLogIndexItem[];

  const updatedItems = existingIndex.filter((item) => item.id !== id);

  await setDoc(
    indexRef,
    {
      items: updatedItems,
      updatedAt: Timestamp.now(),
    },
    { merge: true }
  );
};

export const getChangeLogIndex = async (): Promise<ChangeLogIndexDoc> => {
  const indexRef = doc(db, "changeLog", "changeLogIndex");
  const snap = await getDoc(indexRef);
  if (!snap.exists()) {
    return { items: [], updatedAt: Timestamp.now() };
  }
  return snap.data() as ChangeLogIndexDoc;
};

export const getChangeLogEntry = async (id: string): Promise<ChangeLogEntry> => {
  const entryRef = doc(db, "changeLog", id);
  const snap = await getDoc(entryRef);
  if (!snap.exists()) {
    throw new Error("Change log entry not found");
  }
  return snap.data() as ChangeLogEntry;
};

export const getLatestHighlightsEntry =
  async (): Promise<ChangeLogEntry | null> => {
    const index = await getChangeLogIndex();
    const latest = index.items.find((item) => item.hasHighlights);
    if (!latest) return null;
    return getChangeLogEntry(latest.id);
  };
